import type { ReactNode } from 'react';
import { Building2 } from 'lucide-react';
import { PageHeader, PestanasDeNavegacion } from '@/components/patterns';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { useNombreDeLaEmpresa } from '../hooks/use-nombre-de-la-empresa';

export type PestanaDeMiEmpresa =
  | 'usuarios'
  | 'roles'
  | 'equipos'
  | 'aprobaciones'
  | 'documentos'
  | 'configuracion';

/** Las pestañas de Mi empresa, en el orden del artefacto (Configuración, al final). */
const PESTANAS: Array<{ id: PestanaDeMiEmpresa; texto: string; href: string }> = [
  { id: 'usuarios', texto: 'Usuarios', href: '/users' },
  { id: 'roles', texto: 'Roles', href: '/permisos' },
  { id: 'equipos', texto: 'Equipos', href: '/configuracion/equipos' },
  { id: 'aprobaciones', texto: 'Aprobaciones', href: '/configuracion/aprobaciones' },
  { id: 'documentos', texto: 'Documentos', href: '/pricer-datec/configuracion' },
  { id: 'configuracion', texto: 'Configuración', href: '/settings' },
];

/**
 * El encabezado de las pantallas de Mi empresa (rediseño del 18-oct, artefacto
 * «Mi empresa · Usuarios / Roles / Equipos / Aprobaciones / Documentos»): la
 * píldora con la empresa, el título, la acción principal y las pestañas.
 *
 * Cada pestaña es su propia pantalla con su dirección de siempre (`/users`,
 * `/permisos`…): ningún enlace viejo se rompe. Se ofrece sólo lo que la persona
 * puede abrir: las de administración piden ese rol (como sus rutas) y Documentos
 * —el diseño de la cotización— pide el Pricer.
 */
export function EncabezadoDeMiEmpresa({
  activa,
  titulo,
  subtitulo,
  acciones,
  dataTour,
}: {
  activa: PestanaDeMiEmpresa;
  titulo: ReactNode;
  subtitulo?: ReactNode;
  acciones?: ReactNode;
  /** El paso del tour que apunta al encabezado (`users-header`, `settings-header`…). */
  dataTour?: string;
}) {
  const { isAdmin, canSeeMenu, isModuleLocked } = useAuth();
  const puede = (id: PestanaDeMiEmpresa) =>
    id === 'documentos' ? canSeeMenu('pricer-datec') && !isModuleLocked('pricer-datec') : isAdmin;
  const empresa = useNombreDeLaEmpresa();

  return (
    <div className="space-y-6">
      <div data-tour={dataTour}>
        <PageHeader
          etiqueta={empresa ? `Mi empresa · ${empresa}` : 'Mi empresa'}
          icono={Building2}
          tonoEtiqueta="neutro"
          titulo={titulo}
          subtitulo={subtitulo}
          acciones={acciones}
        />
      </div>
      <PestanasDeNavegacion
        etiqueta="Secciones de Mi empresa"
        pestanas={PESTANAS.filter((p) => p.id === activa || puede(p.id)).map((p) => ({
          texto: p.texto,
          href: p.href,
          activa: p.id === activa,
        }))}
      />
    </div>
  );
}
